import { humanReadableBytes } from '@flakiness/shared/common/utils.js';
import { Task } from '@lit/task';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { Artifact } from '../artifact.js';
import { linkStyles } from './cssstyles.js';

const MAX_INLINE_BYTES = 1024 * 1024;

const TEXT_CONTENT_TYPES = new Set([
  'application/json',
  'application/xml',
  'application/yaml',
  'application/x-yaml',
]);

export function isTextAttachment(artifact: Artifact) {
  const contentType = artifact.contentType.split(';')[0].trim().toLowerCase();
  if (contentType === 'text/html')
    return false;
  if (contentType.startsWith('text/'))
    return true;
  return TEXT_CONTENT_TYPES.has(contentType);
}

@customElement('text-attachment')
class TextAttachment extends LitElement {
  @property({ attribute: false }) artifact?: Artifact;
  @state() private _open: boolean = false;

  private _fetchTask = new Task(this, {
    args: () => [this.artifact, this._open] as const,
    task: async ([artifact, open], { signal }) => {
      if (!artifact || !open)
        return undefined;
      const response = await fetch(artifact.url, { signal });
      if (!response.ok)
        throw new Error(`Failed to load: ${response.status} ${response.statusText}`);
      const contentLength = Number(response.headers.get('content-length') ?? 0);
      if (contentLength > MAX_INLINE_BYTES)
        throw new Error(`File is too large to preview (${humanReadableBytes(contentLength)}). Use the download link.`);
      const blob = await response.blob();
      if (blob.size > MAX_INLINE_BYTES)
        throw new Error(`File is too large to preview (${humanReadableBytes(blob.size)}). Use the download link.`);
      return await blob.text();
    },
  });

  private _toggle() {
    this._open = !this._open;
  }

  override render() {
    const artifact = this.artifact;
    if (!artifact)
      return nothing;
    const chevron = this._open ? 'chevron-down' : 'chevron-right';
    return html`
      <h-box class=header>
        <h-box class=clickable @click=${this._toggle}>
          <sl-icon class=toggle-icon name=${chevron}></sl-icon>
          <sl-icon name=file-earmark-zip></sl-icon>
          <span class=filename>${artifact.fileName}</span>
        </h-box>
        <a class=download href=${artifact.url} download=${artifact.fileName}>
          <sl-icon name=download></sl-icon>
        </a>
      </h-box>
      ${this._open ? this._fetchTask.render({
        initial: () => nothing,
        pending: () => nothing,
        error: (e) => html`<div class=error>${(e as Error).message}</div>`,
        complete: (text) => text === undefined ? nothing : html`<ansi-text .text=${text}></ansi-text>`,
      }) : nothing}
    `;
  }

  static styles = [linkStyles, css`
    :host {
      display: block;
    }

    .header {
      gap: var(--sl-spacing-x-small);
      align-items: center;
      font-size: var(--sl-font-size-small);
    }

    .clickable {
      gap: var(--sl-spacing-2x-small);
      align-items: center;
      cursor: pointer;
      user-select: none;
    }

    .toggle-icon {
      width: 1em;
      height: 1em;
    }

    .filename {
      font-family: var(--sl-font-mono);
    }

    .download {
      display: inline-flex;
      align-items: center;
      color: var(--sl-color-neutral-600);
    }

    .download:hover {
      color: var(--sl-color-primary-600);
    }

    .error {
      color: var(--sl-color-danger-600);
      font-size: var(--sl-font-size-small);
      margin-left: calc(1em + var(--sl-spacing-2x-small));
    }

    ansi-text {
      padding: var(--sl-spacing-medium);
      background-color: var(--sl-color-neutral-50);
      border-radius: var(--sl-border-radius-medium);
      max-height: 300px;
      overflow: auto;
      margin-top: var(--sl-spacing-2x-small);
    }
  `];
}
